import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useGetSet } from "react-use";
import { Message, MessagesSearchResult } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { throttle } from "lodash-es";
import useMessages from "hooks/useMessages";
import emitter, { EVENTS } from "lib/emitter";
import { mergeMessages, oldestRealId } from "lib/mergeMessages";
import { isOptimisticMessage } from "lib/messageMetadata";
import { getMessageSnapshot, setMessageSnapshot } from "lib/messageSnapshots";
import {
  cacheMessages,
  getCachedMessages,
  removeCachedMessage,
  cacheUser,
  getCachedUser,
  cacheThreadIdForUser,
  getCachedThreadIdForUser,
  cacheThreadIdForSlug,
  getCachedThreadIdForSlug,
} from "lib/offlineCache";
import providers from "lib/providers";
import { OptimisticMessage } from "types";
import { Routes } from "../../../../router/config";

// Cursor-pagination page size for scroll-up history loads (id-based `lessThanId`
// cursor instead of an offset `page`, immune to new-message offset drift).
const PAGE_SIZE = 30;

/**
 * Open-a-chat lifecycle:
 *   cold     – nothing cached, network in-flight → show <ChatSkeleton/>
 *   hydrated – cache painted instantly, refreshing in the background → no skeleton
 *   fresh    – network landed and merged into the list
 *   empty    – no cache and the server returned nothing
 */
type LoadPhase = "cold" | "hydrated" | "fresh" | "empty";

export default function useController(
  userSlugOrId: string,
  shouldScrollToBottom: MutableRefObject<boolean>,
  // Called on the hydrated→fresh phase transition (cache rendered first,
  // then network reply landed). The MessagesListView exposes a method that
  // force-pins the viewport to absolute bottom and holds it pinned for
  // ~1.5 s while async height changes settle. Without this, the user
  // ends up slightly above bottom because the last async load (image
  // decode, link preview) often lands after polling's 1 s window has
  // already exited.
  settleAtBottomFnRef?: MutableRefObject<(() => void) | null>
) {
  const [getMessagesState, setMessagesState] = useGetSet<MessagesSearchResult<Message> | null>(null);
  const [getMessageThread, setMessageThread] = useGetSet<MessageThread | null>(null);
  const [phase, setPhase] = useState<LoadPhase>("cold");
  const [senderUser, setSenderUser] = useState<UserPublicData | null>(null);
  const opponentIdRef = useRef<string | null>(null);
  // Chat generation: bumped on every chat switch. Page fetches capture it and
  // drop their result if it changed mid-flight — a page requested for chat A
  // must never merge into chat B's list (mergeMessages keeps out-of-window
  // rows, so a stale merge would visibly plant A's messages inside B).
  const loadGenRef = useRef(0);
  // The skeleton shows ONLY on a true cold open (no cache + network in flight).
  const loadingMessages = phase === "cold";

  const history = useHistory();

  const { markRead } = useMessages();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const markReadThrottled = useCallback(throttle(markRead, 1000), []);

  const onObserve = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    const thread = getMessageThread();
    const messages = getMessagesState();
    const minUnreadMessageId = Number(thread?.minUnreadMessageId ?? "0");

    // Collect all newly-visible unread message IDs in this batch
    const readIds: number[] = [];

    entries.forEach((entry) => {
      if (entry && entry.isIntersecting) {
        const interestingId = Number(entry.target.id.split("message-")[1]);

        if (minUnreadMessageId > 0 && minUnreadMessageId <= interestingId) {
          readIds.push(interestingId);
        }
        observer.unobserve(entry.target);
      }
    });

    if (readIds.length === 0) return;

    // Mark the highest visible message as read (covers all below it)
    const maxReadId = Math.max(...readIds);
    const latestReadMessage = messages?.result.find((m) => m.id === maxReadId.toString());
    if (!latestReadMessage) return;

    markReadThrottled(latestReadMessage)?.then(() => {
      shouldScrollToBottom.current = true;
      setMessageThread((thread) => {
        if (!thread) return thread;
        const lastMessage = messages?.result[messages?.result.length - 1];
        const allRead = lastMessage?.id === maxReadId.toString();
        return {
          ...thread,
          minUnreadMessageId: allRead ? "0" : (maxReadId + 1).toString(),
          unreadMessageCount: Math.max(thread.unreadMessageCount - readIds.length, 0),
        };
      });
    });
  };
  const messageObserver = useRef<IntersectionObserver>(new IntersectionObserver(onObserve));
  const setObserve = (el: HTMLDivElement | null) => {
    if (el) {
      messageObserver.current?.observe(el);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    // Invalidate any in-flight page fetches of the previous chat.
    loadGenRef.current += 1;

    // Clear the previous chat's opponent id so cache hydration can't resolve a
    // stale thread for a not-yet-loaded slug. getUserFromRequest re-sets it.
    opponentIdRef.current = null;

    setPhase("cold");
    // Paint last-known messages from cache immediately (Telegram-style): sync
    // in-memory snapshot first, else IndexedDB. Flips phase → "hydrated" on hit.
    hydrateFromCache(isCancelled);

    (async function () {
      try {
        const localUser = await getUserFromRequest();
        if (cancelled) return;
        setSenderUser(localUser);
        if (!localUser) {
          history.push(Routes.messages);
          return;
        }
        await getMessagesFromRequest(undefined, true, isCancelled);
        if (cancelled) return;
        const hasMsgs = (getMessagesState()?.result?.length ?? 0) > 0;
        // If we rendered cached messages first ("hydrated") and now the
        // fresh data lands (the dominant case after a page reload), pin
        // the viewport to bottom across the cache→fresh swap.
        // MessagesListView's settleAtBottomRef force-jumps to bottom and
        // runs a 1.5 s aggressive watcher window that absorbs async image
        // / link preview / voice metadata loads. Read prev via setPhase's
        // callback form — the captured `phase` from the closure above is
        // stale ("cold" at effect creation).
        setPhase((prev) => {
          if (hasMsgs && prev === "hydrated") {
            settleAtBottomFnRef?.current?.();
          }
          return hasMsgs ? "fresh" : "empty";
        });
      } catch (e) {
        if (cancelled) return;
        // Network refresh failed: keep the cached list visible if we have one,
        // otherwise fall back to the empty-state.
        setPhase((prev) => (prev === "hydrated" ? "hydrated" : "empty"));
      }
    })();

    return () => {
      cancelled = true;
      setMessagesState(null);
      setMessageThread(null);
      setSenderUser(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSlugOrId, history]);

  const loadMore = async (cb?: () => void) => {
    const state = getMessagesState();
    if (state && !state.hasNextPage) return;
    const beforeId = state ? oldestRealId(state.result) : undefined;
    await getMessagesFromRequest(beforeId, false).finally(() => cb && cb());
  };

  // Resolve a cached threadId for this chat WITHOUT a network call, fastest-first.
  const resolveCachedThreadId = async (): Promise<string | null> => {
    const bySlug = getCachedThreadIdForSlug(userSlugOrId); // sync: warm reopen of same URL
    if (bySlug) return bySlug;
    if (/^\d+$/.test(userSlugOrId)) {
      const t = getCachedThreadIdForUser(userSlugOrId); // numeric URL token IS the opponent id
      if (t) return t;
    }
    if (opponentIdRef.current) {
      const t = getCachedThreadIdForUser(opponentIdRef.current);
      if (t) return t;
    }
    const cu = await getCachedUser(userSlugOrId); // cached user (by URL token) → opponent id
    if (cu?.id) {
      const t = getCachedThreadIdForUser(String(cu.id));
      if (t) return t;
    }
    return null;
  };

  // Cache-first paint. Never clobbers a fresh response that already landed.
  const hydrateFromCache = async (isCancelled: () => boolean) => {
    const syncThreadId =
      getCachedThreadIdForSlug(userSlugOrId) ||
      (/^\d+$/.test(userSlugOrId) ? getCachedThreadIdForUser(userSlugOrId) : null);

    // Synchronous in-memory snapshot → zero-flash reopen within the session.
    if (syncThreadId) {
      const snap = getMessageSnapshot(syncThreadId);
      if (snap && snap.length) {
        if (isCancelled()) return;
        setMessagesState((prev) =>
          prev?.result?.length ? prev : ({ result: snap, hasNextPage: true, page: 0 } as any)
        );
        setPhase((p) => (p === "fresh" ? p : "hydrated"));
        return;
      }
    }

    const threadId = syncThreadId || (await resolveCachedThreadId());
    if (!threadId || isCancelled()) return;
    const cached = await getCachedMessages(threadId); // ASC
    if (!cached.length || isCancelled()) return;
    const desc = [...cached].reverse(); // → DESC to match the state convention
    setMessagesState((prev) => (prev?.result?.length ? prev : ({ result: desc, hasNextPage: true, page: 0 } as any)));
    setMessageSnapshot(threadId, desc);
    setPhase((p) => (p === "fresh" ? p : "hydrated"));
  };
  const getUserFromRequest = async (): Promise<UserPublicData | null> => {
    try {
      const isNumericLegacy = /^\d+$/.test(userSlugOrId);
      let foundUser: UserPublicData | null = null;
      if (isNumericLegacy) {
        const response = await providers.UserPublicDataProvider.search({ data: { id: [userSlugOrId] } });
        foundUser = response.result.find((i) => i.id === userSlugOrId) || null;
        if (foundUser?.slug) {
          // Preserve router state (e.g. a jump-to-message target from global
          // search) across the id→slug alias redirect — useJumpToMessage's
          // invalidation treats a chatKey change without state as "user
          // navigated away".
          history.replace(`/messages/${foundUser.slug}`, history.location.state);
        }
      } else {
        foundUser = await providers.UserPublicDataProvider.findBySlug(userSlugOrId);
      }
      if (foundUser) {
        opponentIdRef.current = foundUser.id;
        cacheUser(foundUser).catch(() => {});
      }
      return foundUser;
    } catch (e) {
      // Offline fallback: serve cached user (cache key is the URL token, slug or id)
      if (!navigator.onLine) {
        const cached = await getCachedUser(userSlugOrId);
        if (cached) {
          opponentIdRef.current = (cached as UserPublicData).id;
          return cached as UserPublicData;
        }
      }
      throw e;
    }
  };
  // `beforeId` undefined → newest page; a numeric id → older page strictly
  // before that id (cursor pagination, immune to offset drift).
  const getMessagesFromRequest = async (beforeId?: string, replace = true, isCancelled?: () => boolean) => {
    // Captured chat generation: if the user switches chats while this fetch
    // is in flight, the result must be dropped — merging it would plant the
    // previous chat's messages into the new chat's list.
    const gen = loadGenRef.current;
    const isStale = () => gen !== loadGenRef.current;
    const cancelled = () => !!isCancelled && isCancelled();
    const opponentId = opponentIdRef.current;
    if (!opponentId) return;
    let thread: MessageThread | null = null;
    const messageThread = getMessageThread();
    if (!messageThread || replace) {
      thread = await providers.MessageThreadsProvider.findPersonal({ opponentId }).catch(() => null);
      if (cancelled()) return;
      setMessageThread(thread);
      // Cache the userId/slug → threadId mappings for offline + cache-first lookup
      if (thread?.id) {
        cacheThreadIdForUser(opponentId, String(thread.id));
        cacheThreadIdForSlug(userSlugOrId, String(thread.id));
      }
    }

    const threadId = thread?.id || messageThread?.id;

    // If no thread found and we're offline, try the cached mapping
    if (!threadId && !navigator.onLine) {
      const cachedThreadId = getCachedThreadIdForUser(opponentId);
      if (cachedThreadId) {
        const cached = await getCachedMessages(cachedThreadId);
        if (cached.length > 0 && !cancelled() && !isStale()) {
          const desc = [...cached].reverse(); // cache is ASC → state is DESC
          setMessagesState({ result: desc, hasNextPage: false, page: 0 } as any);
          setMessageSnapshot(cachedThreadId, desc);
          return;
        }
      }
    }

    if (!messageThread && !thread) {
      return;
    }

    let response: MessagesSearchResult<Message>;
    try {
      response = await providers.MessagesProvider.find({
        data: {
          threadId,
          sort: "TIME,DESC",
          size: PAGE_SIZE,
          ...(beforeId ? { lessThanId: beforeId } : {}),
        },
      });
    } catch (e) {
      // Offline fallback: serve cached messages for the newest page
      if (!navigator.onLine && !beforeId && threadId) {
        const cached = await getCachedMessages(String(threadId));
        if (cached.length > 0 && !cancelled() && !isStale()) {
          const desc = [...cached].reverse();
          setMessagesState({ result: desc, hasNextPage: false, page: 0 } as any);
          setMessageSnapshot(String(threadId), desc);
          return;
        }
      }
      throw e;
    }

    if (cancelled()) return;

    // Cache messages for offline access (every page deepens the offline window).
    // Keyed by the FETCHED thread id — correct even if the chat has switched.
    if (threadId && response.result.length > 0) {
      cacheMessages(String(threadId), response.result).catch(() => {});
    }

    // The on-screen list belongs to whatever chat is CURRENT — a stale page
    // must not merge into it.
    if (isStale()) return;

    // Merge the fresh page into whatever is on screen (cache or a previous page)
    // instead of replacing it — keeps scroll/keys stable, no flicker.
    setMessagesState((prevState) => {
      const base = prevState?.result ?? [];
      const result = mergeMessages(base, response.result);
      if (threadId) setMessageSnapshot(String(threadId), result);
      return { ...response, result };
    });
  };

  // Page older history (strictly decreasing `lessThanId` cursor) until
  // `messageId` is in the list state. Used by reply-quote clicks and
  // global-search jumps — the highlight/scroll effect in MessagesListView
  // can only land on a message that exists in `virtualItems`.
  // Bounded: 15 pages ≈ 450 messages.
  const loadUntilMessage = async (messageId: string): Promise<boolean> => {
    const MAX_JUMP_PAGES = 15;
    const gen = loadGenRef.current;
    const startTid = getMessageThread()?.id;
    for (let i = 0; i < MAX_JUMP_PAGES; i++) {
      // Bail if the user switched chats mid-loop — paging would continue
      // with the NEW chat's cursor while hunting the OLD chat's message id.
      if (gen !== loadGenRef.current || getMessageThread()?.id !== startTid) return false;
      const state = getMessagesState();
      if (!state) return false;
      if (state.result.some((m) => String(m.id) === String(messageId))) return true;
      if (!state.hasNextPage) return false;
      const oldest = oldestRealId(state.result);
      // Target id falls inside the already-loaded range but isn't there —
      // the message was deleted; further paging can't find it.
      if (oldest && Number(messageId) > Number(oldest)) return false;
      await getMessagesFromRequest(oldest, false);
    }
    return (getMessagesState()?.result || []).some((m) => String(m.id) === String(messageId));
  };

  const updateMessage = (updated: Message) => {
    const messages = getMessagesState();
    if (!messages) return;
    // Preserve `_optimisticId` if the existing row had one — the React key
    // in MessagesListView prefers it for stable bubble identity. Editing a
    // just-sent message would otherwise flip the key from "_opt_*" to the
    // real id and remount the bubble.
    const result = messages.result.map((m) => {
      if (m.id !== updated.id) return m;
      const prevOpt = (m as { _optimisticId?: string })._optimisticId;
      return prevOpt ? { ...updated, _optimisticId: prevOpt } : updated;
    });
    setMessagesState({ ...messages, result });
    const tid = getMessageThread()?.id;
    if (tid) {
      setMessageSnapshot(String(tid), result);
      // Persist the edit so a reload doesn't hydrate the stale pre-edit copy
      // from IndexedDB (original text would flash for ~1s).
      cacheMessages(String(tid), [updated]).catch(() => {});
    }
  };

  const deleteMessage = (messageId: string) => {
    const messages = getMessagesState();
    if (!messages) return;
    const result = messages.result.filter((m) => m.id !== messageId);
    setMessagesState({ ...messages, result });
    const tid = getMessageThread()?.id;
    if (tid) setMessageSnapshot(String(tid), result);
    // Purge from IndexedDB too — otherwise the cached copy resurrects the
    // message after a page reload (merge is additive).
    removeCachedMessage(messageId).catch(() => {});
  };

  const addMessage = (message: Message) => {
    const messages = getMessagesState();
    const messageThread = getMessageThread();
    if (!messageThread || !messages) {
      getMessagesFromRequest();
      return;
    } else {
      // Prepend the new (newest) message; never trim the oldest. The old
      // `slice(0, -1)` kept a fixed-size window for OFFSET pagination — under
      // cursor (`lessThanId`) pagination there's no fixed window, and dropping
      // the oldest row would evict the MessagesListView `firstItemIndex` anchor
      // and jolt the scroll position on every incoming/sent message.
      const newResult = [message, ...messages.result];
      setMessagesState({ ...messages, result: newResult });
      // Cache new real message for offline access
      if (!isOptimisticMessage(message) && messageThread.id) {
        cacheMessages(String(messageThread.id), [message]).catch(() => {});
      }
      if (messageThread.id) setMessageSnapshot(String(messageThread.id), newResult);
    }
  };

  useEffect(() => {
    const localObserver = messageObserver.current;
    const receivedMessage = (message: Message) => {
      const messages = getMessagesState();
      const messageThread = getMessageThread();
      if (!messageThread) {
        return getMessagesFromRequest();
      }
      if (message.threadId !== messageThread?.id || messages?.result.find((m) => m.id === message.id)) {
        return;
      }
      // If this real message is the server twin of a still-pending optimistic
      // placeholder (matched by clientMessageId), resolve the placeholder IN
      // PLACE instead of adding a second row. This self-heals a placeholder
      // whose send response was lost — without relying on a refetch. We match
      // on the idempotency key (NOT "any pending optimistic from this sender"),
      // so a single stuck placeholder no longer suppresses unrelated messages.
      // Preserve _optimisticId so the React key stays stable (no remount).
      if (!isOptimisticMessage(message) && message.clientMessageId && messages) {
        const placeholder = messages.result.find(
          (m) => isOptimisticMessage(m) && (m as OptimisticMessage).clientMessageId === message.clientMessageId
        ) as OptimisticMessage | undefined;
        if (placeholder) {
          const result = messages.result.map((m) =>
            isOptimisticMessage(m) && (m as OptimisticMessage)._optimisticId === placeholder._optimisticId
              ? ({ ...message, _optimisticId: placeholder._optimisticId } as Message)
              : m
          );
          setMessagesState({ ...messages, result });
          if (messageThread.id) {
            cacheMessages(String(messageThread.id), [message]).catch(() => {});
            setMessageSnapshot(String(messageThread.id), result);
          }
          return;
        }
      }
      // Fallback ONLY for an own echo that carries NO clientMessageId (legacy/
      // system message, or a backend not yet echoing the key) while a send of
      // ours is still in flight: we can't match it precisely, so suppress the
      // duplicate — the send response's resolveOptimistic (or mergeMessages
      // eviction on the next refetch) owns the placeholder→real swap. Without
      // this the echo would be added as a second row AND bump unread for our own
      // message. A message that HAS a clientMessageId but matched no placeholder
      // above is a genuinely distinct message (e.g. sent from another device) —
      // it must NOT be suppressed. Scoped to "sending" (not "failed") so a
      // terminally-failed bubble never suppresses later messages.
      if (!isOptimisticMessage(message) && !message.clientMessageId) {
        const ownSending = messages?.result.find(
          (m) =>
            isOptimisticMessage(m) &&
            (m as OptimisticMessage)._status === "sending" &&
            String((m as OptimisticMessage).senderId) === String(message.senderId)
        );
        if (ownSending) return;
      }
      addMessage(message);
      setMessageThread((thread) => {
        if (!thread) return thread;
        return {
          ...messageThread,
          unreadMessageCount: messageThread.unreadMessageCount + 1,
          minUnreadMessageId: messageThread.minUnreadMessageId === "0" ? message.id : messageThread.minUnreadMessageId,
        };
      });
    };

    const resolveOptimistic = (payload?: { optimisticId: string; message: Message | null; status: string }) => {
      const { optimisticId, message, status } = payload || {};
      if (!optimisticId) return;
      const messages = getMessagesState();
      if (!messages) return;
      if (status === "sent" && message) {
        // Replace optimistic message with real server message.
        // Preserve `_optimisticId` so Virtuoso's React key (see MessagesListView)
        // stays stable across the placeholder→real swap — otherwise the bubble
        // remounts, Virtuoso re-measures from the 80px estimate, and the
        // scroll position drifts (own message scrolls out of view ~1s after send).
        setMessagesState({
          ...messages,
          result: messages.result.map((m) =>
            isOptimisticMessage(m) && m._optimisticId === optimisticId ? { ...message, _optimisticId: optimisticId } : m
          ),
        });
      } else if (status === "failed") {
        // Mark as failed — keep the message but flag it
        setMessagesState({
          ...messages,
          result: messages.result.map((m) =>
            isOptimisticMessage(m) && m._optimisticId === optimisticId ? { ...m, _status: "failed" as const } : m
          ),
        });
      }
    };

    emitter.on<Message>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
    emitter.on(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, resolveOptimistic);
    return () => {
      localObserver.disconnect();
      emitter.off<Message>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
      emitter.off(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, resolveOptimistic);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Thread-specific live subscription — companion to the app-wide
  // `subscribeToAllThreads` in PushManager. The wildcard (threadId="0")
  // subscription drops certain message kinds on the backend, notably
  // `__SYSTEM__:`-prefixed system messages and the JSON call-event
  // messages (e.g. {"type":"call","status":"ENDED"}). Without this
  // per-thread subscription, the in-open chat view only shows those
  // entries after a leave+re-enter (server re-fetch), matching the
  // user-reported "call ended / call started not shown in realtime"
  // symptom. Emitting into MESSAGES_NEW_MESSAGE reuses the existing
  // `receivedMessage` handler, whose `messages.result.find(id===)`
  // guard makes double-fires from the two subscriptions idempotent.
  const currentThreadId = getMessageThread()?.id;
  useEffect(() => {
    if (!currentThreadId) return;
    let sub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    (async () => {
      try {
        const s = await providers.MessagesProvider.subscribeToThread(currentThreadId, (message: Message) => {
          emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, message);
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[1-on-1-chat] subscribeToThread failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [currentThreadId]);

  return {
    messages: getMessagesState()?.result || [],
    thread: getMessageThread(),
    loadingMessages,
    senderUser,
    loadMore,
    scrollMessages: !loadingMessages && (getMessagesState()?.hasNextPage ?? false),
    setObserve,
    minUnreadMessageId: getMessageThread()?.minUnreadMessageId,
    updateMessage,
    deleteMessage,
    loadUntilMessage,
  };
}
